/**
 * The operator's manual.
 *
 * Everything the panel cannot say in a label. Written in the rig's voice:
 * declarative, institutional, no jokes in the instructions.
 */

export interface ManualSection {
  readonly title: string;
  readonly lines: readonly string[];
}

export const MANUAL: readonly ManualSection[] = [
  {
    title: 'What this is',
    lines: [
      'A brain in a jar, wired to a clock you control. It produces Sparks.',
      'You spend Sparks on hardware that produces more Sparks.',
      'Running the clock above its rated speed produces far more, and generates heat.',
      'At 100 degrees the brain melts, and everything it was thinking comes out as one idea.',
      'That is not a failure state. It is the whole point: you are farming ideas out of a brain by overworking it.',
    ],
  },
  {
    title: 'Sparks',
    lines: [
      'The working currency. The large number in the centre of the panel.',
      'The jar produces a small trickle on its own, so you are never stuck at zero.',
      'Sparks are spent on generators, and are destroyed by a meltdown.',
    ],
  },
  {
    title: 'Why climb the ladder',
    lines: [
      'A tier cannot be opened until you own ten of the tier below it. The ladder is climbed one rung at a time, and the base is never optional.',
      'Running cells costs heat on top of whatever the clock costs, and how much depends on where your sparks come from. A rig leaning on Neurons runs about four times hotter than the same output drawn from the top of the ladder.',
      'That is the reason to climb. Not more sparks for the money - the same sparks, for less heat, which means longer runs before the meltdown.',
      'The Core Temp panel breaks it down: how much heat comes from the clock, from the cells, from the void, and what cooling takes back off.',
    ],
  },
  {
    title: 'The feed chain',
    lines: [
      'Every tier is fed by the tier below it. A Dendrite needs five Neurons behind it, an Axon needs two Dendrites, and so on down to the base.',
      'A couple of units of any tier run unfed, so opening a new row is never dead on arrival. Past that, units without enough beneath them fire at 40%.',
      'Nothing you buy is ever wasted, but three Dendrites resting on one Neuron is three Dendrites mostly idling. Each row says how many of its units are fed, and names what it is short of.',
      'This is why the base matters. A Neuron produces very little on its own; what makes it worth buying is that five of them bring a Dendrite up to full rate, and a Dendrite is worth far more.',
      'The requirement multiplies all the way down, so a single Cerebrum implies roughly three hundred Neurons underneath it. The brain is a pyramid, and it has to be built like one.',
    ],
  },
  {
    title: 'Generators',
    lines: [
      'Eight tiers, from a single Neuron up to a whole Cerebrum.',
      'Each one adds a flat amount to your output per second, forever.',
      'Every unit you buy of a tier makes the next unit of that tier 12% more expensive, so no single row carries you forever.',
      'Every 25 units of a tier multiplies that tier by eight, permanently. The bar on each row shows how far off the next one is.',
      'A higher tier costs about eleven times more and produces about eleven times more, but starts at the bottom of its own price curve, so a fresh row is usually the better buy.',
      'You do not have to work that out. Each row shows how much output per thought it gives compared to the best row available, and the best one is marked. Buying it is never wrong.',
      'Old rows do not go dead. Those two numbers are set against each other: 25 units deeper costs about seventeen times more and pays about eight times more, so backfilling an old tier stays worth doing once a new one gets expensive.',
    ],
  },
  {
    title: 'The clock',
    lines: [
      'Rated speed is 100%. The lever is locked there until you own ten Neurons.',
      'Output scales with clock to the power of 1.5. Heat scales with clock to the power of 2.6.',
      'Because heat climbs faster than output, every setting above 100% is a countdown rather than a choice you make once.',
      'At exactly 100% heat generation and cooling are equal, so the rig holds its temperature. Once the intrusive thoughts arrive this is no longer indefinite - they add heat of their own - but it remains the coolest setting that still produces.',
      'Below 100% the rig actively cools, at the cost of output.',
    ],
  },
  {
    title: 'Core temperature',
    lines: [
      'Zero to 100. The needle is driven directly by the real value with no smoothing.',
      'Heat is not only a threat. Output gets a bonus that grows with the square of temperature, worth up to 60% at the redline.',
      'That bonus arrives almost entirely in the last few degrees: at 50 degrees it is worth 15%, at 99 degrees it is worth 59%.',
      'Riding hot is therefore worth real money, and the whole game is deciding how close you are willing to sit before you take the idea and start again.',
    ],
  },
  {
    title: 'Vent coolant',
    lines: [
      'Instantly removes 40 degrees, then needs 45 seconds to refill.',
      'The cooldown is the constraint. There is no coolant resource to manage.',
      'Used well, a vent at 97 degrees keeps the temperature bonus alive far longer than the raw curve suggests. Keyboard shortcut: V.',
    ],
  },
  {
    title: 'Forcing an idea',
    lines: [
      'A brain that thinks hard enough, for long enough, eventually has one idea. Getting it out destroys the brain.',
      'This is the point of the game, not an accident. You are farming ideas.',
      'It happens on its own at 100 degrees, or you can force it deliberately at any time with the button.',
      'Either way it destroys the current run: all Sparks, and all generators.',
      'What you keep is Ideas, worked out from the total Sparks that run produced.',
      'The panel shows the figure live as Idea Yield, so you can watch it grow while you decide whether to push further.',
      'Forcing it is always good once the yield is worth having. The only mistakes are forcing it too early, or holding so long that you have stopped earning.',
      'A run that ends short of a whole idea is not wasted. The fraction it did produce carries over into the next run, so short hot runs add up instead of paying nothing. The panel shows how much has been carried.',
    ],
  },
  {
    title: 'Intrusive thoughts',
    lines: [
      'After your third meltdown, something else starts showing up in the jar.',
      'The longer a single run goes on, the more of them crowd in. The panel shows the split: the brain on one side, the void on the other.',
      'While the brain holds the room they cost nothing. Once the void has the upper hand, holding it back starts generating heat, and that heat climbs the longer you stay.',
      'This means no run lasts forever, at any clock speed. Even at 100%, where the rig cools exactly as fast as it heats, the void will eventually cook it.',
      'That is the point. The question stops being how hot you dare run and becomes how long you dare stay.',
      'The Defence memories buy you time: Antibodies, Adaptive Response and a Quiet Room each multiply how long the brain holds the room.',
    ],
  },
  {
    title: 'Ideas and Memories',
    lines: [
      'Ideas are the permanent currency. Never lost, at any temperature, for any reason.',
      'They are spent on Memories: upgrades that carry across every future run. Sparks make ideas, ideas become memories, and memories are what make the brain better at making sparks.',
      'This is the only thing that makes progress: each run is meant to end, and to leave you better equipped than it found you.',
      'A run of a million Sparks is worth about ten ideas; a billion is worth about a hundred and fifty. The curve is deliberately flat, so a run ten times longer is not ten times better.',
    ],
  },
  {
    title: 'How a session actually goes',
    lines: [
      'Buy generators until the clock unlocks. Push the clock until the temperature makes you nervous. Pull back, or vent, and learn where the edge is.',
      'When the yield is worth having, force the idea and spend it.',
      'Repeat. Runs get shorter and hotter as cooling upgrades let you hold higher clocks.',
      'A run that ends in six to ten minutes is normal early on. Two to three minutes is normal once the tree is filling in.',
    ],
  },
];
